import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  BookOpen, Save, Download, Upload, Printer, Calendar, FolderOpen,
  HardDrive, RefreshCw, FileText, AlertCircle, CheckCircle, Clock,
  Archive, FileSpreadsheet, Sparkles, X, Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import BulkUpload from '../components/BulkUpload';
import ImportAll from '../components/ImportAll';
import BackupRestore from '../components/BackupRestore';
import ExportData from '../components/ExportData';

const FilePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const getContent = () => {
    if (path.includes('/file/books')) return <OpenCloseBooks />;
    if (path.includes('/file/backup')) return <BackupRestore />;
    if (path.includes('/file/import-all')) return <ImportAll />;
    if (path.includes('/file/import-export')) return <ImportExport />;
    if (path.includes('/file/reports')) return <ReportCenter />;
    if (path.includes('/file/year-end')) return <YearEndClose />;
    return <FileOverview navigate={navigate} />;
  };

  return (
    <div className="space-y-6">
      {getContent()}
    </div>
  );
};

// Overview page with quick links
const FileOverview = ({ navigate }) => {
  const quickActions = [
    { icon: BookOpen, label: 'Open / Close Books', path: '/file/books', color: 'bg-sky-100 text-sky-700' },
    { icon: Save, label: 'Backup & Restore', path: '/file/backup', color: 'bg-emerald-100 text-emerald-700' },
    { icon: Download, label: 'Import / Export', path: '/file/import-export', color: 'bg-purple-100 text-purple-700' },
    { icon: Upload, label: 'Import All Existing Data', path: '/file/import-all', color: 'bg-teal-100 text-teal-700' },
    { icon: Printer, label: 'Report Center', path: '/file/reports', color: 'bg-amber-100 text-amber-700' },
    { icon: Calendar, label: 'Year-End Close', path: '/file/year-end', color: 'bg-rose-100 text-rose-700' },
  ];

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">File Management</h1>
        <p className="text-gray-500 mt-1">Manage your books, backups, imports, and reports</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickActions.map((action, idx) => {
          const Icon = action.icon;
          return (
            <Card 
              key={idx} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(action.path)}
              data-testid={`file-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className={`p-3 rounded-lg ${action.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{action.label}</h3>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};

// Open / Close Books
const OpenCloseBooks = () => {
  const [books, setBooks] = useState([
    { id: 1, company: 'Blue Skies Farm LLC', year: '2025-2026', status: 'open', lastAccess: '2 hours ago' },
    { id: 2, company: 'Blue Skies Farm LLC', year: '2024-2025', status: 'closed', lastAccess: '3 months ago' },
    { id: 3, company: 'Sunrise Orchards Inc', year: '2025-2026', status: 'open', lastAccess: '1 day ago' },
  ]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBook, setNewBook] = useState({ company: '', year: '' });

  const toggleBookStatus = (bookId) => {
    setBooks(prev => prev.map(book => {
      if (book.id === bookId) {
        const newStatus = book.status === 'open' ? 'closed' : 'open';
        toast.success(`${book.company} (${book.year}) is now ${newStatus}`);
        return { ...book, status: newStatus, lastAccess: 'Just now' };
      }
      return book;
    }));
  };

  const createNewBook = () => {
    if (!newBook.company || !newBook.year) {
      toast.error('Please fill in all fields');
      return;
    }
    const book = {
      id: books.length + 1,
      company: newBook.company,
      year: newBook.year,
      status: 'open',
      lastAccess: 'Just now'
    };
    setBooks(prev => [...prev, book]);
    setShowCreateModal(false);
    setNewBook({ company: '', year: '' });
    toast.success(`Created new books for ${book.company}`);
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Open / Close Books</h1>
        <p className="text-gray-500 mt-1">Select and manage your company books</p>
      </div>

      <div className="grid gap-4">
        {books.map((book) => (
          <Card key={book.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${book.status === 'open' ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                  <BookOpen className={`w-6 h-6 ${book.status === 'open' ? 'text-emerald-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{book.company}</h3>
                  <p className="text-sm text-gray-500">Fiscal Year: {book.year}</p>
                  <p className="text-xs text-gray-400">Last accessed: {book.lastAccess}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  book.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  {book.status === 'open' ? 'Open' : 'Closed'}
                </span>
                <Button 
                  variant={book.status === 'open' ? 'outline' : 'default'} 
                  size="sm"
                  onClick={() => toggleBookStatus(book.id)}
                  data-testid={`toggle-book-${book.id}`}
                >
                  {book.status === 'open' ? 'Close' : 'Open'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-dashed border-2 border-slate-300 bg-slate-50/50">
        <CardContent className="p-6 text-center">
          <FolderOpen className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-700">Create New Books</h3>
          <p className="text-sm text-slate-500 mt-1">Start a new company or fiscal year</p>
          <Button className="mt-4" onClick={() => setShowCreateModal(true)} data-testid="create-new-books-btn">
            Create New Books
          </Button>
        </CardContent>
      </Card>

      {/* Create New Books Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Create New Books</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowCreateModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input 
                  placeholder="Enter company name"
                  value={newBook.company}
                  onChange={(e) => setNewBook(prev => ({ ...prev, company: e.target.value }))}
                  data-testid="new-book-company"
                />
              </div>
              <div className="space-y-2">
                <Label>Fiscal Year</Label>
                <Input 
                  placeholder="e.g., 2025-2026"
                  value={newBook.year}
                  onChange={(e) => setNewBook(prev => ({ ...prev, year: e.target.value }))}
                  data-testid="new-book-year"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button onClick={createNewBook} data-testid="confirm-create-book">Create</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// Import / Export
const ImportExport = () => {
  const navigate = useNavigate();
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [aiValidation, setAiValidation] = useState(false);


  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Import / Export</h1>
        <p className="text-gray-500 mt-1">Transfer data in and out of your books</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowBulkUpload(true)}>
          <CardContent className="p-6 text-center">
            <Upload className="w-12 h-12 text-sky-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Import Data</h3>
            <p className="text-sm text-gray-500 mt-2">Import timecards, expenses, customers, vendors, employees, invoices or journal entries from CSV, Excel (.xlsx, .xls) or dBase (.dbf) files</p>
            <Button className="mt-4" data-testid="start-import-btn">Start Import</Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer border-teal-300" onClick={() => navigate('/file/import-all')} data-testid="import-all-card">
          <CardContent className="p-6 text-center">
            <Upload className="w-12 h-12 text-teal-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Import All Existing Data</h3>
            <p className="text-sm text-gray-500 mt-2">Bring a client&apos;s whole data set across, from AgData Blue Skies (dBase) or another accounting program (CSV / Excel): setup information, G/L transactions and payroll</p>
            <Button className="mt-4 bg-teal-600 hover:bg-teal-700" data-testid="start-import-all-btn">Import All</Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowExportModal(true)}>
          <CardContent className="p-6 text-center">
            <Download className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Export Data</h3>
            <p className="text-sm text-gray-500 mt-2">Download a client's data as Excel, CSV or JSON</p>
            <Button variant="outline" className="mt-4" data-testid="start-export-btn">Start Export</Button>
          </CardContent>
        </Card>
      </div>

      {/* AI Validation */}
      <Card className="bg-gradient-to-r from-purple-50 to-sky-50 border-purple-200">
        <CardContent className="p-4 flex items-center gap-4">
          <Sparkles className="w-8 h-8 text-purple-500" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">AI Import Validator</h3>
            <p className="text-sm text-gray-600">AI validates imported file mapping before posting to catch errors early</p>
          </div>
          <Button 
            variant={aiValidation ? 'default' : 'outline'} 
            className={aiValidation ? '' : 'border-purple-300'}
            onClick={() => {
              setAiValidation(!aiValidation);
              toast.success(aiValidation ? 'AI Validation disabled' : 'AI Validation enabled');
            }}
            data-testid="enable-ai-validation"
          >
            {aiValidation ? 'Enabled' : 'Enable AI Validation'}
          </Button>
        </CardContent>
      </Card>

      <BulkUpload isOpen={showBulkUpload} onClose={() => setShowBulkUpload(false)} />

      <ExportData open={showExportModal} onClose={() => setShowExportModal(false)} />
    </>
  );
};

// Report Center
const ReportCenter = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [printQueue, setPrintQueue] = useState([]);
  const [showPrintQueue, setShowPrintQueue] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('PDF');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const reports = [
    { category: 'Financial', items: [
      { name: 'Profit & Loss', id: 'profit_loss' },
      { name: 'Balance Sheet', id: 'balance_sheet' },
      { name: 'Cash Flow Statement', id: 'cash_flow' },
      { name: 'Trial Balance', id: 'trial_balance' }
    ]},
    { category: 'Accounts', items: [
      { name: 'AR Aging', id: 'ar_aging' },
      { name: 'AP Aging', id: 'ap_aging' },
      { name: 'Customer Balances', id: 'customer_balances' },
      { name: 'Vendor Balances', id: 'vendor_balances' }
    ]},
    { category: 'Payroll', items: [
      { name: 'Payroll Summary', id: 'payroll_summary' },
      { name: 'Tax Reports', id: 'tax_reports' },
      { name: 'Employee Earnings', id: 'employee_earnings' },
      { name: 'Deduction Summary', id: 'deduction_summary' }
    ]},
    { category: 'Management', items: [
      { name: 'Budget vs Actual', id: 'budget_vs_actual' },
      { name: 'Department Expenses', id: 'department_expenses' },
      { name: 'Cost Center Analysis', id: 'cost_center_analysis' }
    ]},
  ];

  const handleReportClick = (report) => {
    setSelectedReport(report);
    // Set default dates
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setShowReportModal(true);
  };

  const generateReport = async () => {
    if (!selectedReport) return;
    
    setIsGenerating(true);
    toast.loading(`Generating ${selectedReport.name}...`);
    
    try {
      const response = await fetch(`${API_URL}/api/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          report_type: selectedReport.id,
          start_date: startDate,
          end_date: endDate,
          format: selectedFormat.toLowerCase()
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      // Get the file blob
      const blob = await response.blob();
      
      // Create download link with correct extension
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const ext = selectedFormat.toLowerCase() === 'excel' ? 'xlsx' : 'pdf';
      a.download = `${selectedReport.name.replace(/\s+/g, '_')}_${endDate}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.dismiss();
      toast.success(`${selectedReport.name} (${selectedFormat}) downloaded successfully!`);
      setShowReportModal(false);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.dismiss();
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const addToPrintQueue = () => {
    setPrintQueue(prev => [...prev, { id: Date.now(), name: selectedReport.name, reportId: selectedReport.id, startDate, endDate, status: 'pending' }]);
    toast.success(`${selectedReport.name} added to print queue`);
    setShowReportModal(false);
  };

  const generateFromQueue = async (item) => {
    toast.loading(`Generating ${item.name}...`);
    try {
      const response = await fetch(`${API_URL}/api/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: item.reportId,
          start_date: item.startDate,
          end_date: item.endDate,
          format: 'pdf'
        }),
      });

      if (!response.ok) throw new Error('Failed to generate report');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.name.replace(/\s+/g, '_')}_${item.endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.dismiss();
      toast.success(`${item.name} downloaded!`);
      setPrintQueue(prev => prev.filter(p => p.id !== item.id));
    } catch (error) {
      toast.dismiss();
      toast.error(`Failed to generate ${item.name}`);
    }
  };

  const generateAllFromQueue = async () => {
    for (const item of printQueue) {
      await generateFromQueue(item);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Report Center</h1>
          <p className="text-gray-500 mt-1">Generate and download branded PDF reports</p>
        </div>
        <Button onClick={() => setShowPrintQueue(true)} data-testid="print-queue-btn">
          <Printer className="w-4 h-4 mr-2" />
          Print Queue ({printQueue.length})
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {reports.map((group, idx) => (
          <Card key={idx}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{group.category} Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {group.items.map((item, i) => (
                  <button
                    key={i}
                    className="w-full flex items-center gap-2 p-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                    onClick={() => handleReportClick(item)}
                    data-testid={`report-${item.id}`}
                  >
                    <FileText className="w-4 h-4 text-gray-400" />
                    {item.name}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Report Modal */}
      {showReportModal && selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{selectedReport.name}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowReportModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-sky-50 rounded-lg text-sm text-sky-700">
                <strong>Blue Skies Farm LLC</strong> - Branded PDF report with company name, colors, and contact information.
              </div>
              <div className="space-y-2">
                <Label>Date Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input 
                    type="date" 
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="report-start-date" 
                  />
                  <Input 
                    type="date" 
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="report-end-date" 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Format</Label>
                <div className="flex gap-2">
                  {['PDF', 'Excel'].map((format) => (
                    <button 
                      key={format}
                      className={`px-3 py-2 border rounded-lg text-sm ${selectedFormat === format ? 'bg-sky-100 border-sky-500' : 'hover:bg-gray-50'}`}
                      onClick={() => setSelectedFormat(format)}
                      data-testid={`format-${format.toLowerCase()}`}
                    >
                      {format}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={addToPrintQueue}>Add to Queue</Button>
                <Button onClick={generateReport} disabled={isGenerating} data-testid="generate-report">
                  {isGenerating ? 'Generating...' : `Download ${selectedFormat}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Print Queue Modal */}
      {showPrintQueue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Print Queue</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowPrintQueue(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {printQueue.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No reports in queue</p>
              ) : (
                <div className="space-y-2">
                  {printQueue.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.startDate} to {item.endDate}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => generateFromQueue(item)}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setPrintQueue(prev => prev.filter(p => p.id !== item.id))}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button className="w-full mt-4" onClick={generateAllFromQueue}>
                    <Download className="w-4 h-4 mr-2" />
                    Download All ({printQueue.length})
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// Year-End Close
const YearEndClose = () => {
  const [steps, setSteps] = useState([
    { id: 1, label: 'Review Unposted Batches', status: 'pending', description: 'Post all pending transactions' },
    { id: 2, label: 'Bank Reconciliation', status: 'pending', description: 'Reconcile all bank accounts' },
    { id: 3, label: 'AR/AP Review', status: 'pending', description: 'Review outstanding balances' },
    { id: 4, label: 'Payroll Close', status: 'pending', description: 'Process final payroll and tax forms' },
    { id: 5, label: 'Generate Tax Reports', status: 'pending', description: 'Create year-end tax documents' },
    { id: 6, label: 'Close Fiscal Year', status: 'locked', description: 'Final close and rollover' },
  ]);
  const [activeStep, setActiveStep] = useState(null);
  const [showStepModal, setShowStepModal] = useState(false);

  const handleStartStep = (step) => {
    if (step.status === 'locked') return;
    setActiveStep(step);
    setShowStepModal(true);
  };

  const completeStep = () => {
    setSteps(prev => {
      const updated = prev.map(s => {
        if (s.id === activeStep.id) {
          return { ...s, status: 'complete' };
        }
        // Unlock next step if current is completed
        if (s.id === activeStep.id + 1 && s.status === 'locked') {
          return { ...s, status: 'pending' };
        }
        return s;
      });
      return updated;
    });
    toast.success(`${activeStep.label} completed!`);
    setShowStepModal(false);
    setActiveStep(null);
  };

  const allStepsComplete = steps.filter(s => s.id < 6).every(s => s.status === 'complete');

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Year-End / Period Close Wizard</h1>
        <p className="text-gray-500 mt-1">Complete these steps to close your fiscal period</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {steps.map((step, idx) => (
              <div key={step.id} className="p-4 flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step.status === 'complete' ? 'bg-emerald-100 text-emerald-700' : 
                    step.status === 'locked' ? 'bg-slate-100 text-slate-400' : 'bg-sky-100 text-sky-700'}`}>
                  {step.status === 'complete' ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{step.label}</h3>
                  <p className="text-sm text-gray-500">{step.description}</p>
                </div>
                <Button 
                  variant={step.status === 'locked' ? 'ghost' : step.status === 'complete' ? 'outline' : 'default'} 
                  size="sm"
                  disabled={step.status === 'locked' && !allStepsComplete}
                  onClick={() => handleStartStep(step)}
                  data-testid={`step-${step.id}`}
                >
                  {step.status === 'complete' ? 'Review' : step.status === 'locked' ? (allStepsComplete ? 'Start' : 'Locked') : 'Start'}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Important Notice</h3>
            <p className="text-sm text-amber-700 mt-1">
              Complete all steps in order before closing the fiscal year. Once closed, transactions cannot be added to this period.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Step Modal */}
      {showStepModal && activeStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{activeStep.label}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowStepModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">{activeStep.description}</p>
              
              {activeStep.id === 1 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">Unposted Batches Found: 3</p>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li>• AP Batch #1024 - $5,420.00</li>
                    <li>• JE Batch #512 - $1,200.00</li>
                    <li>• Payroll Batch #89 - $12,500.00</li>
                  </ul>
                </div>
              )}

              {activeStep.id === 2 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">Bank Accounts to Reconcile:</p>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li>• Operating Account - Last reconciled: Dec 31</li>
                    <li>• Payroll Account - Last reconciled: Dec 31</li>
                  </ul>
                </div>
              )}

              {activeStep.id === 6 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-medium text-red-800">Warning: This action is irreversible</p>
                  <p className="text-sm text-red-700 mt-1">Closing the fiscal year will lock all transactions and carry forward balances to the new year.</p>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowStepModal(false)}>Cancel</Button>
                {activeStep.status === 'complete' ? (
                  <Button variant="outline">View Details</Button>
                ) : (
                  <Button onClick={completeStep} data-testid="complete-step">
                    {activeStep.id === 6 ? 'Close Fiscal Year' : 'Mark Complete'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default FilePage;
